declare const __LM_WIDGET_API_BASE_URL__: string;
declare const __LM_WIDGET_ALLOW_API_OVERRIDE__: boolean;

export const DEFAULT_API_BASE_URL = __LM_WIDGET_API_BASE_URL__;
export const ALLOW_API_OVERRIDE = __LM_WIDGET_ALLOW_API_OVERRIDE__;

export type WidgetView = "list" | "tiles" | "calendar";

export interface WidgetOptions {
  key: string;
  view: WidgetView;
  limit?: number;
  columns?: number;
  showImage: boolean;
  showTitle: boolean;
  showVenue: boolean;
  showArtists: boolean;
  showTimezone: boolean;
  scrollable: boolean;
  maxHeight: number;
  apiBaseUrl: string;
}

export function readWidgetOptions(element: HTMLElement): WidgetOptions | null {
  const key = element.dataset.key?.trim();
  if (!key) {
    return null;
  }

  const view = element.dataset.view === "calendar" ? "calendar" : (element.dataset.view === "tiles" ? "tiles" : "list");
  const limit = parsePositiveInteger(element.dataset.limit);
  const columns = parseOptionalBoundedInteger(element.dataset.columns, 1, 6);
  const apiBaseUrl = readApiBaseUrl(element);

  return {
    key,
    view,
    ...(limit ? { limit } : {}),
    ...(columns ? { columns } : {}),
    showImage: readBoolean(element.dataset.showImage, false),
    showTitle: readBoolean(element.dataset.showTitle, true),
    showVenue: readBoolean(element.dataset.showVenue, true),
    showArtists: readBoolean(element.dataset.showArtists, true),
    showTimezone: readBoolean(element.dataset.showTimezone, false),
    scrollable: readBoolean(element.dataset.scrollable, false),
    maxHeight: parseBoundedInteger(element.dataset.maxHeight, 180, 800, 360),
    apiBaseUrl,
  };
}

function readApiBaseUrl(element: HTMLElement): string {
  if (ALLOW_API_OVERRIDE && element.dataset.apiBaseUrl) {
    return element.dataset.apiBaseUrl;
  }
  return DEFAULT_API_BASE_URL;
}

function parsePositiveInteger(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function parseBoundedInteger(value: string | undefined, min: number, max: number, fallback: number): number {
  if (!value) {
    return fallback;
  }
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(Math.max(parsed, min), max);
}

function parseOptionalBoundedInteger(value: string | undefined, min: number, max: number): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  return Math.min(Math.max(parsed, min), max);
}

function readBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) {
    return fallback;
  }
  return value !== "false";
}
